import React from 'react';
import { makeStyles } from '@material-ui/styles';
import cn from 'clsx';
import ButtonBase from '../BaseButton';

const TagButton: React.FC<ButtonProps> = props => {
    const { selected, before, after, className, children, ...domProps } = props;
    const classes = useStyles();
    const classNames = cn(classes.root, selected && classes.selected, className);

    return (
        <ButtonBase className={classNames} {...domProps}>
            {before && <div className={classes.surroundings}>{before}</div>}
            <div className={classes.text}>{children}</div>
            {after && <div className={classes.surroundings}>{after}</div>}
        </ButtonBase>
    );
};

const useStyles = makeStyles(
    ({ palette, typography, mixBlendMode }) => ({
        root: {
            ...typography.t71,
            color: palette.greys[700],
            lineHeight: 1.15,
            transition: 'all 300ms ease-in-out',
            display: 'flex',
            alignItems: 'center',
            maxWidth: '100%',
            padding: '.5em',
            border: `1px solid ${palette.greys[700]}`,
            borderRadius: 4,

            '& > * + *': {
                marginLeft: '.5em',
            },

            '&:hover': {
                backgroundColor: `${palette.greys[700]}10`,
                mixBlendMode: mixBlendMode,
            },

            '&:disabled': {
                pointerEvents: 'none',
                border: `1px solid ${palette.greys[400]}`,
                color: palette.greys[400],
            },
        },
        surroundings: {
            display: 'flex',
            alignItems: 'center',
            flexGrow: 0,
            flexShrink: 0,
            '&:first-child': { marginLeft: -1 },
            '&:last-child': { marginRight: -1 },
        },
        text: {
            textAlign: 'center',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            flexGrow: 1,
        },
        selected: {
            color: palette.greys[100],
            backgroundColor: palette.greys[700],

            '&:hover': {
                backgroundColor: palette.greys[600],
            },

            '&:disabled': {
                pointerEvents: 'none',
                color: palette.greys[100],
                backgroundColor: palette.greys[400],
            },
        },
    }),
    { classNamePrefix: 'TagButton' }
);

type ButtonProps = JSX.IntrinsicElements['button'] & {
    selected?: boolean;
    before?: React.ReactNode;
    after?: React.ReactNode;
};

export default TagButton;
